import { Link } from 'react-router-dom';

const InvestmentTiers = () => {
  const tiers = [
    {
      tier: 'Joint Venture',
      scope: 'Manufacturing',
      commitment: '$5M – $50M',
      example: 'New palm-oil refinery',
      description: 'Plant-scale production facilities',
      icon: 'ri-handshake-line',
    },
    {
      tier: 'Export Distribution',
      scope: 'Regional Trade Hubs',
      commitment: '$0.5M – $2M',
      example: 'Southeast-Asia logistics',
      description: 'Distribution network expansion',
      icon: 'ri-ship-line',
    },
    {
      tier: 'Tech Innovation',
      scope: 'SaaS & Agritech',
      commitment: '$1M – $5M',
      example: 'KL Carpool, B2B e-Marketplace',
      description: 'Digital platform development',
      icon: 'ri-lightbulb-line',
    },
  ];

  const partners = [
    { name: 'Affin Bank', logo: 'ri-bank-line' },
    { name: 'Exim Bank Malaysia', logo: 'ri-bank-card-line' },
    { name: 'Citibank Singapore', logo: 'ri-global-line' },
  ];

  const partnerTypes = [
    {
      type: 'Active Partner',
      icon: 'ri-user-star-line',
      description: 'Hands-on involvement in operations, strategy, and decision-making. Ideal for investors who want direct influence over business growth.',
      benefits: ['Board-level participation', 'Operational oversight', 'Strategic decision rights', 'Higher equity stake'],
    },
    {
      type: 'Non-Active Partner',
      icon: 'ri-money-dollar-circle-line',
      description: 'Capital investment with passive returns. Perfect for investors seeking high-yield returns with quarterly interim profit sharing, without day-to-day management responsibilities.',
      benefits: ['Passive income stream', 'Quarterly interim profit sharing', 'Regular performance reports', 'Flexible exit options'],
    },
  ];

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:mb-6">
            Unlock High-Yield Investment Opportunities
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Transparent, tiered investment options backed by <strong>30+ years of proven track record</strong> and strategic banking partnerships
          </p>
          <div className="mt-4 inline-flex items-center space-x-2 px-3 sm:px-5 py-2 bg-accent-50 border border-accent-200 rounded-full">
            <i className="ri-information-line text-accent-600"></i>
            <span className="text-xs sm:text-sm text-accent-700 font-medium">New investors &amp; partners are limited to involvement in one division/subsidiary company only</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Tier</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Scope</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Typical Commitment</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Example Projects</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tiers.map((tier, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg">
                          <i className={`${tier.icon} text-xl text-primary-600`}></i>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{tier.tier}</div>
                          <div className="text-sm text-gray-500">{tier.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6 text-gray-700">{tier.scope}</td>
                    <td className="px-6 py-6">
                      <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-semibold whitespace-nowrap">
                        {tier.commitment}
                      </span>
                    </td>
                    <td className="px-6 py-6 text-gray-700">{tier.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Choose Your Partnership Style */}
        <div className="mb-12">
          <div className="text-center mb-8 sm:mb-10">
            <h3 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 mb-4">
              Choose Our Partnership as Your Shortcut to Success
            </h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you want hands-on control or passive returns — we have the perfect investment structure for you
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partnerTypes.map((pt, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-primary-400 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-xl">
                    <i className={`${pt.icon} text-3xl text-primary-600`}></i>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900">{pt.type}</h4>
                  </div>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">{pt.description}</p>
                <ul className="space-y-3 mb-6">
                  {pt.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-double-line text-primary-600"></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/investor-relations"
                  className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors cursor-pointer"
                >
                  <span>Explore This Option</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Strategic Banking Partners
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 flex flex-col items-center justify-center space-y-3 shadow-md"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className={`${partner.logo} text-3xl text-primary-600`}></i>
                </div>
                <span className="text-gray-900 font-semibold text-center">{partner.name}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/investor-relations"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <span>Start Your Investment Journey</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestmentTiers;
